import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API_KEY = 'AIzaSyBf2sm6XUpkffu_dLadOWVk0dC4PYz5-ZY';

export default function AddressAutocomplete({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  onPlaceSelected
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const sessionToken = useRef(Math.random().toString(36).substring(7));

  // Fetch autocomplete suggestions
  useEffect(() => {
    if (!value || value.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          'https://places.googleapis.com/v1/places:autocomplete',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': API_KEY
            },
            body: JSON.stringify({
              input: value,
              sessionToken: sessionToken.current,
              includedRegionCodes: ['us'],
              languageCode: 'en'
            })
          }
        );

        const data = await response.json();

        if (data.suggestions) {
          setSuggestions(data.suggestions);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [value]);

  // Handle suggestion selection
  const handleSelect = async (suggestion) => {
    try {
      const placeId = suggestion.placePrediction.placeId;

      // Fetch place details
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': API_KEY,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress'
          }
        }
      );

      const place = await response.json();

      if (!place.addressComponents) {
        return;
      }

      // Extract address components
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let zipCode = '';

      place.addressComponents.forEach(component => {
        const types = component.types;

        if (types.includes('street_number')) {
          streetNumber = component.longText;
        }
        if (types.includes('route')) {
          route = component.longText;
        }
        if (types.includes('locality')) {
          city = component.longText;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.shortText;
        }
        if (types.includes('postal_code')) {
          zipCode = component.longText;
        }
      });

      // Set street address
      const streetAddress = `${streetNumber} ${route}`.trim();
      onChange(streetAddress || place.formattedAddress || '');

      // Call callback with extracted data
      if (onPlaceSelected) {
        onPlaceSelected({ city, state, zipCode });
      }

      // Reset for next search
      sessionToken.current = Math.random().toString(36).substring(7);
      setShowDropdown(false);
      setSuggestions([]);
    } catch (err) {
      console.error('Error processing place:', err);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full relative"
      ref={inputRef}
    >
      <label htmlFor={id} className="block text-sm font-medium text-charcoal mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <input
        type="text"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`
          w-full px-4 py-3
          border-2 rounded-lg
          text-charcoal
          placeholder-gray-400
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-onyx-green focus:border-transparent
          ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 hover:border-gray-400'}
        `}
        required={required}
        autoComplete="off"
      />

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {showDropdown && suggestions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => handleSelect(suggestion)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-charcoal">
                    {suggestion.placePrediction.text.text}
                  </span>
                </div>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      {isLoading && (
        <div className="absolute right-3 top-11 text-gray-400">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 text-sm text-red-500"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}
